import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import Card from "../components/Card";
import Button from "../components/Button";
import Alert from "../components/Alert";

// Helper to get pharmacyId from JWT
const getPharmacyId = () => {
  try {
    const token = localStorage.getItem("userToken");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userId;
  } catch {
    return null;
  }
};

const ProductsList = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", information: "", price: "" });
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const API_BASE_URL = 'https://api.tenarx.net';
  const pharmacyId = getPharmacyId();

  // Fetch products for this pharmacy
  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("userToken");
      const res = await fetch(`${API_BASE_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setProducts((data.data || []).filter((p) => p.pharmacyId === pharmacyId));
      } else {
        setError(data.message || "Failed to fetch products");
      }
    } catch (err) {
      setError("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    setDeleting(id);
    try {
      const token = localStorage.getItem("userToken");
      const res = await fetch(`${API_BASE_URL}/api/products/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setProducts((prev) => prev.filter((p) => p._id !== id));
      } else {
        alert(data.message || "Failed to delete product");
      }
    } catch {
      alert("Failed to delete product");
    } finally {
      setDeleting(null);
    }
  };

  const startEdit = (product) => {
    setEditId(product._id);
    setEditForm({ name: product.name, information: product.information, price: product.price });
    setEditError(null);
  };

  const handleEditInput = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim() || !editForm.information.trim() || !editForm.price) {
      setEditError("All fields are required.");
      return;
    }
    if (isNaN(editForm.price) || Number(editForm.price) <= 0) {
      setEditError("Price must be a positive number.");
      return;
    }
    setEditLoading(true);
    try {
      const token = localStorage.getItem("userToken");
      const res = await fetch(`http://localhost:5000/api/products/${editId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setProducts((prev) => prev.map((p) => (p._id === editId ? { ...p, ...editForm } : p)));
        setEditId(null);
      } else {
        setEditError(data.message || "Failed to update product");
      }
    } catch {
      setEditError("Failed to update product");
    } finally {
      setEditLoading(false);
    }
  };

  const filtered = products.filter((p) =>
    (categoryFilter === "" || p.category === categoryFilter) &&
    (p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.information.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Layout title="Your Products">
      <div className="max-w-4xl mx-auto py-8">
        <Card>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
            <h2 className="text-xl font-bold">Your Products</h2>
            <div className="flex gap-2 w-full md:w-auto">
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
              />
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="medicine">Medicine</option>
                <option value="supplement">Supplement</option>
                <option value="equipment">Equipment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : error ? (
            <Alert type="error" message={error} />
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No products found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 border">Image</th>
                    <th className="p-2 border">Name</th>
                    <th className="p-2 border">Information</th>
                    <th className="p-2 border">Category</th>
                    <th className="p-2 border">Price</th>
                    <th className="p-2 border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p._id} className="border-b">
                      <td className="p-2 border">
  {p.image ? (
    <img
      src={p.image.startsWith("http") ? p.image : `${API_BASE_URL}${p.image}`}
      alt={p.name}
      className="w-16 h-12 object-cover rounded"
      onError={(e) => {
        console.warn("Image failed to load:", p.image);
        e.currentTarget.src = "https://via.placeholder.com/64x48?text=No+Image";
      }}
    />
  ) : (
    <span className="text-gray-400">No image</span>
  )}
</td>

                      <td className="p-2 border">
                        {editId === p._id ? (
                          <input
                            name="name"
                            value={editForm.name}
                            onChange={handleEditInput}
                            className="px-2 py-1 border rounded w-full"
                          />
                        ) : (
                          p.name
                        )}
                      </td>
                      <td className="p-2 border">
                        {editId === p._id ? (
                          <input
                            name="information"
                            value={editForm.information}
                            onChange={handleEditInput}
                            className="px-2 py-1 border rounded w-full"
                          />
                        ) : (
                          p.information
                        )}
                      </td>
                      <td className="p-2 border">
                        {p.category}
                      </td>
                      <td className="p-2 border">
  {editId === p._id ? (
    <input
      name="price"
      type="number"
      value={editForm.price}
      onChange={handleEditInput}
      className="px-2 py-1 border rounded w-full"
    />
  ) : (
    `${p.price} ETB`
  )}
</td>

                      <td className="p-2 border space-x-2">
                        {editId === p._id ? (
                          <>
                            <Button size="sm" onClick={handleEditSave} disabled={editLoading}>
                              {editLoading ? "Saving..." : "Save"}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => setEditId(null)}>
                              Cancel
                            </Button>
                            {editError && <div className="text-xs text-red-500 mt-1">{editError}</div>}
                          </>
                        ) : (
                          <>
                            <Button size="sm" onClick={() => startEdit(p)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => handleDelete(p._id)} disabled={deleting === p._id}>
                              {deleting === p._id ? "Deleting..." : "Delete"}
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default ProductsList; 